import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '@/components/ui/Modal'
import { FormField } from '@/components/ui/FormField'
import type { Client, Measurement, MeasurementType } from '@/types'

const addMeasurementSchema = z.object({
  type: z.enum(['weight', 'body-fat', 'muscle-mass', 'circumference', 'blood-pressure', 'heart-rate', 'vo2-max', 'flexibility', 'strength-test']),
  value: z.number().min(0, 'Value must be positive'),
  unit: z.string().min(1, 'Unit is required'),
  bodyPart: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  notes: z.string().optional()
})

type AddMeasurementFormData = z.infer<typeof addMeasurementSchema>

interface AddMeasurementModalProps {
  client: Client
  isOpen: boolean
  onClose: () => void
  onSave: (client: Client) => void
}

export function AddMeasurementModal({ client, isOpen, onClose, onSave }: AddMeasurementModalProps) {
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset
  } = useForm<AddMeasurementFormData>({
    resolver: zodResolver(addMeasurementSchema),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      type: 'weight'
    }
  })

  const measurementType = watch('type')

  const getDefaultUnit = (type: MeasurementType): string => {
    switch (type) {
      case 'weight': return 'lbs'
      case 'body-fat': return '%'
      case 'muscle-mass': return 'lbs'
      case 'circumference': return 'inches'
      case 'blood-pressure': return 'mmHg'
      case 'heart-rate': return 'bpm'
      case 'vo2-max': return 'ml/kg/min'
      case 'flexibility': return 'inches'
      case 'strength-test': return 'lbs'
      default: return ''
    }
  }

  const showBodyPartField = measurementType === 'circumference' || measurementType === 'flexibility'

  const handleSave = async (data: AddMeasurementFormData) => {
    setIsLoading(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const newMeasurement: Measurement = {
        id: `measurement-${Date.now()}`,
        clientId: client.id,
        type: data.type,
        value: data.value,
        unit: data.unit,
        bodyPart: data.bodyPart,
        date: new Date(data.date),
        notes: data.notes,
        takenBy: 'coach'
      }
      
      const updatedClient: Client = {
        ...client,
        measurements: [...client.measurements, newMeasurement],
        updatedAt: new Date()
      }
      
      onSave(updatedClient)
    } catch (error) {
      console.error('Failed to add measurement:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Measurement"
      size="md"
    >
      <form onSubmit={handleSubmit(handleSave)} className="space-y-6">
        <div className="grid grid-cols-1 gap-4">
          <FormField
            label="Measurement Type"
            htmlFor="type"
            required
            error={errors.type?.message}
          >
            <select
              id="type"
              className="coach-input"
              {...register('type')}
            >
              <option value="weight">Weight</option>
              <option value="body-fat">Body Fat %</option>
              <option value="muscle-mass">Muscle Mass</option>
              <option value="circumference">Circumference</option>
              <option value="blood-pressure">Blood Pressure</option>
              <option value="heart-rate">Heart Rate</option>
              <option value="vo2-max">VO2 Max</option>
              <option value="flexibility">Flexibility</option>
              <option value="strength-test">Strength Test</option>
            </select>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Value"
              htmlFor="value"
              required
              error={errors.value?.message}
            >
              <input
                id="value"
                type="number"
                step="0.1"
                className="coach-input"
                placeholder="0.0"
                {...register('value', { valueAsNumber: true })}
              />
            </FormField>

            <FormField
              label="Unit"
              htmlFor="unit"
              required
              error={errors.unit?.message}
            >
              <input
                id="unit"
                type="text"
                className="coach-input"
                placeholder={getDefaultUnit(measurementType)}
                {...register('unit')}
              />
            </FormField>
          </div>

          {showBodyPartField && (
            <FormField
              label="Body Part"
              htmlFor="bodyPart"
              error={errors.bodyPart?.message}
            >
              <input
                id="bodyPart"
                type="text"
                className="coach-input"
                placeholder="e.g., Chest, Waist, Bicep"
                {...register('bodyPart')}
              />
            </FormField>
          )}

          <FormField
            label="Date"
            htmlFor="date"
            required
            error={errors.date?.message}
          >
            <input
              id="date"
              type="date"
              className="coach-input"
              {...register('date')}
            />
          </FormField>

          <FormField
            label="Notes"
            htmlFor="notes"
            error={errors.notes?.message}
          >
            <textarea
              id="notes"
              className="coach-input"
              rows={3}
              placeholder="Optional notes about the measurement..."
              {...register('notes')}
            />
          </FormField>
        </div>

        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="coach-button-secondary px-4 py-2"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="coach-button-primary px-4 py-2"
            disabled={isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Measurement'}
          </button>
        </div>
      </form>
    </Modal>
  )
}